import { useState, useEffect, useCallback } from 'react'
import { Table2, Save, ExternalLink, Plus, Trash2, ChevronUp, ChevronDown, RotateCcw } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import { inputStyle, cardStyle, labelStyle, hintStyle, smallBtn, iconBtn, textareaStyle, subCardStyle, rowHeadStyle, rowLabelStyle, Field, Area, StringListEditor, Toggle } from './ui'

// Placements — the PUBLIC "Sponsorship Ad Placements" page (watch.tenband.tv/ad-placements),
// linked from the Sponsors page. Stored in app_settings (placements_* keys), delivered via
// vader /api/app-config → watch /api/config → Placements.jsx.
//
// The page has four parts, each editable below:
//   1. Primary matrix   — one row per offering (creative format / where / delivery / example)
//   2. Availability     — placement × Live / On-Demand / 24-7 Linear, with checkmarks
//   3. Signature        — the six "own a moment" sponsorship examples
//   4. CTA              — closing heading, body, and two button labels
//
// The editor seeds itself from GET /api/app-config `sponsorships` — that block already merges
// saved settings over the worker's DEFAULT_PLACEMENTS_* built-ins, so what you see here is
// exactly what the public page renders. "Restore defaults" clears the JSON keys and re-reads.
//
// NOTE: the editor components live at module scope on purpose. Defined inside the page
// component they'd be a new component type on every render, so React would unmount and
// remount the inputs mid-typing and the field would lose focus after each keystroke.

// Friendly names for the reorderable sections. Keys must match PLACEMENT_SECTIONS in the
// vader worker; an unknown key still renders (labelled by its raw key) rather than vanishing.
const SECTION_LABELS = {
  events: { label: 'Upcoming events row', note: 'Cards built from tagged content-library items' },
  offerings: { label: 'Placement matrix', note: 'The four offerings and their detail columns' },
  availability: { label: 'Programming availability', note: 'Placement × Live / On-Demand / 24×7 Linear' },
  signature: { label: 'Signature sponsorships', note: 'Own a Memorable Part of the Broadcast' },
  cta: { label: 'Closing call to action', note: 'Heading, body, and the two buttons' },
}

const ICON_CHOICES = [
  { value: 'banner', label: 'Banner / QR' },
  { value: 'play', label: 'Play button (video)' },
  { value: 'mic', label: 'Microphone (reads)' },
  { value: 'star', label: 'Star (signature)' },
]

/* ---------------- page -------------------------------------------------------- */

export default function Placements() {
  const { api } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')

  const [enabled, setEnabled] = useState(true)
  const [heading, setHeading] = useState('')
  const [intro, setIntro] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [disclaimer, setDisclaimer] = useState('')
  const [matrixColumns, setMatrixColumns] = useState([])
  const [offerings, setOfferings] = useState([])
  const [availability, setAvailability] = useState({ enabled: true, heading: '', intro: '', columns: [], rows: [] })
  const [signature, setSignature] = useState({ enabled: true, heading: '', intro: '', items: [] })
  const [cta, setCta] = useState({ enabled: true, heading: '', body: '', primary_label: '', secondary_label: '' })
  // Upcoming-events row. `resolved` is what the worker actually matched for the SAVED tag —
  // read-only feedback so the operator can tell "nothing tagged yet" from "tag misspelled".
  const [events, setEvents] = useState({ enabled: true, heading: '', intro: '', tag: '', limit: 6, layout: 'row' })
  const [resolved, setResolved] = useState({ tag: '', items: [] })
  const [allTags, setAllTags] = useState([])
  const [sectionOrder, setSectionOrder] = useState([])

  // Seed from the effective config (saved values merged over worker defaults).
  const load = useCallback(async () => {
    setLoading(true)
    try {
      const cfg = await api('/app-config')
      const s = cfg?.sponsorships || {}
      setEnabled(s.enabled !== false)
      setHeading(s.heading || '')
      setIntro(s.intro || '')
      setContactEmail(s.contact_email || '')
      setDisclaimer(s.disclaimer || '')
      setMatrixColumns(Array.isArray(s.matrix_columns) ? s.matrix_columns : [])
      setOfferings(
        (Array.isArray(s.offerings) ? s.offerings : []).map(o => ({
          id: o.id || '',
          num: o.num || '',
          name: o.name || '',
          icon: o.icon || 'banner',
          summary: o.summary || '',
          example: o.example || '',
          enabled: o.enabled !== false,
          format: Array.isArray(o.format) ? o.format : [],
          where: Array.isArray(o.where) ? o.where : [],
          delivery: Array.isArray(o.delivery) ? o.delivery : [],
        }))
      )
      const av = s.availability || {}
      setAvailability({
        enabled: av.enabled !== false,
        heading: av.heading || '',
        intro: av.intro || '',
        columns: Array.isArray(av.columns) ? av.columns : [],
        rows: (Array.isArray(av.rows) ? av.rows : []).map(r => ({
          label: r.label || '',
          note: r.note || '',
          cells: (Array.isArray(r.cells) ? r.cells : []).map(c => ({ on: !!c.on, note: c.note || '' })),
        })),
      })
      const sg = s.signature || {}
      setSignature({
        enabled: sg.enabled !== false,
        heading: sg.heading || '',
        intro: sg.intro || '',
        items: (Array.isArray(sg.items) ? sg.items : []).map(i => ({ name: i.name || '', how: i.how || '', value: i.value || '' })),
      })
      // Arrives complete + de-duplicated from the worker (normalizePlacementOrder).
      setSectionOrder(Array.isArray(s.section_order) ? s.section_order : [])
      const evc = s.events || {}
      setEvents({
        enabled: evc.enabled !== false,
        heading: evc.heading || '',
        intro: evc.intro || '',
        tag: evc.tag || '',
        limit: evc.limit || 6,
        layout: evc.layout === 'grid' ? 'grid' : 'row',
      })
      setResolved({
        tag: evc.tag || '',
        items: Array.isArray(evc.items) ? evc.items : [],
        diagnostics: evc.diagnostics || null,
      })
      const c = s.cta || {}
      setCta({
        enabled: c.enabled !== false,
        heading: c.heading || '',
        body: c.body || '',
        primary_label: c.primary_label || '',
        secondary_label: c.secondary_label || '',
      })
    } catch (e) {
      setStatus(`Error loading: ${e.message}`)
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { load() }, [load])

  // Existing tag names, for the tag field's autocomplete — picking from this list is what
  // stops the row silently matching nothing because of a typo.
  useEffect(() => {
    let cancelled = false
    api('/tags')
      .then(d => { if (!cancelled) setAllTags(Array.isArray(d?.tags) ? d.tags : []) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [api])

  /* ---- offerings ---- */

  const updateOffering = (idx, patch) =>
    setOfferings(prev => prev.map((o, i) => (i === idx ? { ...o, ...patch } : o)))

  function addOffering() {
    setOfferings(prev => [
      ...prev,
      {
        id: `offering-${prev.length + 1}`,
        num: String(prev.length + 1).padStart(2, '0'),
        name: 'New offering',
        icon: 'banner',
        summary: '',
        example: '',
        enabled: true,
        format: [],
        where: [],
        delivery: [],
      },
    ])
  }
  function removeOffering(idx) {
    if (!confirm('Remove this offering from the Sponsors page?')) return
    setOfferings(prev => prev.filter((_, i) => i !== idx))
  }
  function moveOffering(idx, dir) {
    const to = idx + dir
    if (to < 0 || to >= offerings.length) return
    setOfferings(prev => {
      const next = [...prev]
      const [m] = next.splice(idx, 1)
      next.splice(to, 0, m)
      return next
    })
  }

  /* ---- availability ---- */

  const cellCount = Math.max(0, availability.columns.length - 1)

  const updateAvailRow = (ri, patch) =>
    setAvailability(prev => ({ ...prev, rows: prev.rows.map((r, i) => (i === ri ? { ...r, ...patch } : r)) }))

  const updateAvailCell = (ri, ci, patch) =>
    setAvailability(prev => ({
      ...prev,
      rows: prev.rows.map((r, i) =>
        i === ri ? { ...r, cells: r.cells.map((c, j) => (j === ci ? { ...c, ...patch } : c)) } : r
      ),
    }))

  function addAvailRow() {
    setAvailability(prev => ({
      ...prev,
      rows: [...prev.rows, { label: 'New placement', note: '', cells: Array.from({ length: cellCount }, () => ({ on: true, note: '' })) }],
    }))
  }
  function removeAvailRow(ri) {
    setAvailability(prev => ({ ...prev, rows: prev.rows.filter((_, i) => i !== ri) }))
  }
  function moveAvailRow(ri, dir) {
    const to = ri + dir
    if (to < 0 || to >= availability.rows.length) return
    setAvailability(prev => {
      const rows = [...prev.rows]
      const [m] = rows.splice(ri, 1)
      rows.splice(to, 0, m)
      return { ...prev, rows }
    })
  }

  /* ---- signature ---- */

  const updateSigItem = (i, patch) =>
    setSignature(prev => ({ ...prev, items: prev.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) }))

  /* ---- save / reset ---- */

  async function save() {
    // Client-side guard mirroring the worker's validators, so a bad payload fails here with a
    // clear message instead of a 400 from /settings.
    if (matrixColumns.length !== 5) { setStatus('Error: the matrix needs exactly 5 column headings.'); return }
    const ids = new Set()
    for (const o of offerings) {
      const id = o.id.trim()
      if (!id) { setStatus(`Error: offering "${o.name || '(unnamed)'}" needs an id.`); return }
      if (ids.has(id)) { setStatus(`Error: duplicate offering id "${id}".`); return }
      ids.add(id)
      if (!o.name.trim()) { setStatus(`Error: offering "${id}" needs a name.`); return }
    }
    if (availability.columns.length < 2) { setStatus('Error: the availability table needs at least 2 columns.'); return }
    for (const [i, r] of availability.rows.entries()) {
      if (!r.label.trim()) { setStatus(`Error: availability row ${i + 1} needs a label.`); return }
      if (r.cells.length !== cellCount) { setStatus(`Error: availability row ${i + 1} needs exactly ${cellCount} cells.`); return }
    }
    for (const [i, it] of signature.items.entries()) {
      if (!it.name.trim()) { setStatus(`Error: signature example ${i + 1} needs a name.`); return }
    }

    setSaving(true); setStatus('')
    try {
      const payload = {
        placements_enabled: enabled ? '1' : '0',
        placements_heading: heading.trim(),
        placements_intro: intro.trim(),
        placements_contact_email: contactEmail.trim(),
        placements_disclaimer: disclaimer.trim(),
        placements_matrix_columns: JSON.stringify(matrixColumns),
        placements_offerings: JSON.stringify(
          offerings.map(o => ({
            id: o.id.trim(),
            num: o.num.trim(),
            name: o.name.trim(),
            icon: o.icon,
            summary: o.summary.trim(),
            example: o.example.trim(),
            enabled: o.enabled,
            format: o.format.map(s => s.trim()).filter(Boolean),
            where: o.where.map(s => s.trim()).filter(Boolean),
            delivery: o.delivery.map(s => s.trim()).filter(Boolean),
          }))
        ),
        placements_availability: JSON.stringify({
          enabled: availability.enabled,
          heading: availability.heading.trim(),
          intro: availability.intro.trim(),
          columns: availability.columns,
          rows: availability.rows.map(r => ({
            label: r.label.trim(),
            note: r.note.trim(),
            cells: r.cells.map(c => ({ on: !!c.on, note: (c.note || '').trim() })),
          })),
        }),
        placements_signature: JSON.stringify({
          enabled: signature.enabled,
          heading: signature.heading.trim(),
          intro: signature.intro.trim(),
          items: signature.items.map(i => ({ name: i.name.trim(), how: i.how.trim(), value: i.value.trim() })),
        }),
        placements_section_order: JSON.stringify(sectionOrder),
        placements_events_enabled: events.enabled ? '1' : '0',
        placements_events_heading: events.heading.trim(),
        placements_events_intro: events.intro.trim(),
        placements_events_tag: events.tag.trim().toLowerCase(),
        placements_events_limit: String(Math.max(1, Math.min(24, parseInt(events.limit, 10) || 6))),
        placements_events_layout: events.layout === 'grid' ? 'grid' : 'row',
        placements_cta_enabled: cta.enabled ? '1' : '0',
        placements_cta_heading: cta.heading.trim(),
        placements_cta_body: cta.body.trim(),
        placements_cta_primary_label: cta.primary_label.trim(),
        placements_cta_secondary_label: cta.secondary_label.trim(),
      }
      await api('/settings', { method: 'PUT', body: JSON.stringify({ settings: payload }) })
      setStatus('Saved. The Sponsors page reflects changes within ~5 min (/api/config cache).')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  // Clear the JSON keys → /api/app-config hands back the worker's built-in defaults.
  async function restoreDefaults() {
    if (!confirm('Discard your placement content and restore the built-in defaults?')) return
    setSaving(true); setStatus('')
    try {
      await api('/settings', {
        method: 'PUT',
        body: JSON.stringify({
          settings: {
            placements_matrix_columns: '', placements_offerings: '',
            placements_availability: '', placements_signature: '',
          },
        }),
      })
      await load()
      setStatus('Restored the built-in defaults.')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div style={{ color: 'var(--color-text-muted)' }}>Loading…</div>
  }

  return (
    <div>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, margin: '0 0 20px', lineHeight: 1.5, display: 'flex', gap: 8 }}>
        <Table2 size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          The public <strong>Sponsorship Ad Placements</strong> page (<code>watch.tenband.tv/ad-placements</code>) —
          what a sponsorship actually puts on the air. The “Shown/Hidden” toggle below controls the link to it from
          the Sponsors page. Prices live on the <strong>Sponsorship Packages</strong> tab.
        </span>
      </p>

      {/* Section */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>Section</h3>
        <Toggle on={enabled} onClick={() => setEnabled(v => !v)}>
          Show the link to the Placements page on the Sponsors page.
        </Toggle>
        <Field label="Heading" value={heading} onChange={setHeading} />
        <Area
          label="Intro"
          value={intro}
          onChange={setIntro}
          rows={6}
          hint="Two paragraphs — separate them with a blank line."
        />
        <Field
          label="Sponsorship contact email"
          value={contactEmail}
          onChange={setContactEmail}
          placeholder="sales@tenbandmedia.com"
          hint="Used by every “Contact us” link and the Request a Custom Proposal button."
        />
        <Area
          label="Disclaimer"
          value={disclaimer}
          onChange={setDisclaimer}
          rows={2}
          hint="Footnote under the tables — where frequency and volume claims get their caveat."
        />
      </div>

      {/* Section order */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 4px' }}>Section order</h3>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '0 0 12px' }}>
          The order these blocks appear down the public Placements page. A section that is switched off, or that
          has nothing to show, is skipped without leaving a gap.
        </p>
        {sectionOrder.map((key, i) => {
          const meta = SECTION_LABELS[key] || { label: key, note: '' }
          return (
            <div
              key={key}
              style={{ ...subCardStyle, display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, padding: '10px 12px' }}
            >
              <span style={{ ...rowLabelStyle, minWidth: 18 }}>{i + 1}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{meta.label}</div>
                {meta.note && <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{meta.note}</div>}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  type="button"
                  title="Move up"
                  disabled={i === 0}
                  onClick={() => setSectionOrder(prev => {
                    const n = [...prev]; const [m] = n.splice(i, 1); n.splice(i - 1, 0, m); return n
                  })}
                  style={{ ...iconBtn, opacity: i === 0 ? 0.35 : 1 }}
                >
                  <ChevronUp size={13} />
                </button>
                <button
                  type="button"
                  title="Move down"
                  disabled={i === sectionOrder.length - 1}
                  onClick={() => setSectionOrder(prev => {
                    const n = [...prev]; const [m] = n.splice(i, 1); n.splice(i + 1, 0, m); return n
                  })}
                  style={{ ...iconBtn, opacity: i === sectionOrder.length - 1 ? 0.35 : 1 }}
                >
                  <ChevronDown size={13} />
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Upcoming events row */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 4px' }}>Upcoming events row</h3>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '0 0 12px' }}>
          An optional card row near the top of the Placements page, built from content library items carrying a
          tag. Tag an event in the Content Catalog and it appears here automatically — nothing to maintain twice.
        </p>
        <Toggle on={events.enabled} onClick={() => setEvents(p => ({ ...p, enabled: !p.enabled }))}>
          Show the upcoming events row.
        </Toggle>
        <Field label="Heading" value={events.heading} onChange={v => setEvents(p => ({ ...p, heading: v }))} />
        <Area label="Intro" value={events.intro} onChange={v => setEvents(p => ({ ...p, intro: v }))} rows={2} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 120px', gap: 10 }}>
          <div>
            <label style={labelStyle}>Tag</label>
            <input
              type="text"
              list="placements-tag-list"
              value={events.tag}
              onChange={e => setEvents(p => ({ ...p, tag: e.target.value }))}
              placeholder="tbtv-upcoming-events"
              style={inputStyle}
              spellCheck={false}
              autoComplete="off"
            />
            <datalist id="placements-tag-list">
              {allTags.map(t => <option key={t} value={t} />)}
            </datalist>
            <p style={hintStyle}>
              Pick an existing tag or type a new one — it is matched case-insensitively.
              {allTags.length ? ` ${allTags.length} tags exist.` : ''}
            </p>
          </div>
          <div>
            <label style={labelStyle}>Layout</label>
            <select
              value={events.layout}
              onChange={e => setEvents(p => ({ ...p, layout: e.target.value }))}
              style={inputStyle}
            >
              <option value="row">Scrolling row</option>
              <option value="grid">Card grid</option>
            </select>
            <p style={hintStyle}>
              {events.layout === 'grid'
                ? 'Wraps onto multiple lines.'
                : 'One line, scrolls sideways.'}
            </p>
          </div>
          <div>
            <label style={labelStyle}>Max cards</label>
            <input
              type="number"
              min={1}
              max={24}
              value={events.limit}
              onChange={e => setEvents(p => ({ ...p, limit: e.target.value }))}
              style={inputStyle}
            />
          </div>
        </div>

        {/* What the SAVED tag currently resolves to — the fastest way to see why the row is
            empty (nothing tagged / not active / already finished) without leaving the page. */}
        <div style={{ marginTop: 6, padding: '10px 12px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-text-muted)', marginBottom: 6 }}>
            Currently matching <code>{resolved.tag || '—'}</code>
          </div>
          {resolved.items.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.5 }}>
              No items showing. An item appears when it carries this <strong>tag</strong> and its scheduled start
              has not already passed (undated items always show). Catalog status is <strong>not</strong> a
              requirement — drafts show too. Tag events in Content Catalog, then reload this page; the public page
              follows within ~5 min.
            </p>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: 'var(--color-text)' }}>
              {resolved.items.map(i => (
                <li key={i.id} style={{ marginBottom: 3 }}>
                  {i.title}
                  <span style={{ color: 'var(--color-text-muted)' }}>
                    {i.scheduled_start ? ` — ${i.scheduled_start}` : ' — undated'}
                    {i.status ? ` · ${i.status}` : ''}
                    {i.thumbnail ? '' : ' · no thumbnail'}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* Counts straight from the worker's query — turns "why is it empty?" into a fact. */}
          {resolved.diagnostics && (
            <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '8px 0 0', lineHeight: 1.6 }}>
              {resolved.diagnostics.tagged} item{resolved.diagnostics.tagged === 1 ? '' : 's'} carry this tag
              {resolved.diagnostics.dropped_past > 0 && ` · ${resolved.diagnostics.dropped_past} hidden (already past)`}
              {resolved.diagnostics.dropped_untitled > 0 && ` · ${resolved.diagnostics.dropped_untitled} hidden (no title)`}
              {Object.keys(resolved.diagnostics.statuses || {}).length > 0 &&
                ` · statuses: ${Object.entries(resolved.diagnostics.statuses).map(([k, v]) => `${v} ${k}`).join(', ')}`}
              {resolved.diagnostics.error && ` · query error: ${resolved.diagnostics.error}`}
              {` · showing up to ${resolved.items.length >= (parseInt(events.limit, 10) || 6) ? 'the max' : 'all'} of them`}
            </p>
          )}
          {events.tag.trim().toLowerCase() !== (resolved.tag || '') && (
            <p style={{ fontSize: 11, color: 'var(--color-primary, #dc2626)', margin: '8px 0 0' }}>
              Tag changed — save to see what the new tag matches.
            </p>
          )}
        </div>
      </div>

      {/* Matrix column headings */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 4px' }}>Matrix columns</h3>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '0 0 12px' }}>
          The five headings across the top of the primary matrix. Order is fixed: offering, creative format,
          where it appears, how it is delivered, example.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
          {matrixColumns.map((c, i) => (
            <input
              key={i}
              type="text"
              value={c}
              onChange={e => setMatrixColumns(matrixColumns.map((x, j) => (j === i ? e.target.value : x)))}
              style={{ ...inputStyle, fontSize: 12 }}
            />
          ))}
        </div>
      </div>

      {/* Offerings */}
      {offerings.map((o, idx) => (
        <div key={idx} style={{ ...cardStyle, opacity: o.enabled ? 1 : 0.6 }}>
          <div style={rowHeadStyle}>
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
              {o.num ? `${o.num} · ` : ''}{o.name || '(unnamed offering)'}
            </h3>
            <div style={{ display: 'flex', gap: 4 }}>
              <button type="button" title="Move up" onClick={() => moveOffering(idx, -1)} style={iconBtn}><ChevronUp size={13} /></button>
              <button type="button" title="Move down" onClick={() => moveOffering(idx, 1)} style={iconBtn}><ChevronDown size={13} /></button>
              <button
                type="button"
                onClick={() => updateOffering(idx, { enabled: !o.enabled })}
                style={{ ...smallBtn, minWidth: 74, justifyContent: 'center', color: o.enabled ? 'var(--color-text)' : 'var(--color-text-muted)' }}
              >
                {o.enabled ? 'Shown' : 'Hidden'}
              </button>
              <button type="button" title="Remove offering" onClick={() => removeOffering(idx)} style={iconBtn}><Trash2 size={13} /></button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 150px', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Number</label>
              <input type="text" value={o.num} onChange={e => updateOffering(idx, { num: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Name</label>
              <input type="text" value={o.name} onChange={e => updateOffering(idx, { name: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>ID</label>
              <input type="text" value={o.id} onChange={e => updateOffering(idx, { id: e.target.value })} style={inputStyle} spellCheck={false} />
            </div>
            <div>
              <label style={labelStyle}>Icon</label>
              <select value={o.icon} onChange={e => updateOffering(idx, { icon: e.target.value })} style={inputStyle}>
                {ICON_CHOICES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <Area label="Summary" value={o.summary} onChange={v => updateOffering(idx, { summary: v })} rows={2} hint="One line under the offering name in the matrix." />
          <StringListEditor label={matrixColumns[1] || 'Creative Format'} items={o.format} onChange={v => updateOffering(idx, { format: v })} />
          <StringListEditor label={matrixColumns[2] || 'Where It Appears'} items={o.where} onChange={v => updateOffering(idx, { where: v })} />
          <StringListEditor label={matrixColumns[3] || 'How It Is Delivered'} items={o.delivery} onChange={v => updateOffering(idx, { delivery: v })} />
          <Area
            label={matrixColumns[4] || 'Example Sponsor Execution'}
            value={o.example}
            onChange={v => updateOffering(idx, { example: v })}
            rows={3}
            hint="Rendered in a tinted “Example” box so it reads differently from the descriptive copy."
          />
        </div>
      ))}

      <button type="button" onClick={addOffering} style={{ ...smallBtn, marginBottom: 16 }}>
        <Plus size={13} /> Add offering
      </button>

      {/* Availability */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>Programming availability</h3>
        <Toggle on={availability.enabled} onClick={() => setAvailability(p => ({ ...p, enabled: !p.enabled }))}>
          Show the compact availability grid.
        </Toggle>
        <Field label="Heading" value={availability.heading} onChange={v => setAvailability(p => ({ ...p, heading: v }))} />
        <Area label="Intro" value={availability.intro} onChange={v => setAvailability(p => ({ ...p, intro: v }))} rows={2} />

        <label style={labelStyle}>Columns</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8, marginBottom: 6 }}>
          {availability.columns.map((c, i) => (
            <input
              key={i}
              type="text"
              value={c}
              onChange={e => setAvailability(p => ({ ...p, columns: p.columns.map((x, j) => (j === i ? e.target.value : x)) }))}
              style={{ ...inputStyle, fontSize: 12 }}
            />
          ))}
        </div>
        <p style={{ ...hintStyle, marginBottom: 14 }}>
          First column labels the row; the rest are the content types compared. Changing the number of columns
          requires editing every row’s cells — leave these four as-is unless you are adding a content type.
        </p>

        {availability.rows.map((r, ri) => (
          <div key={ri} style={subCardStyle}>
            <div style={rowHeadStyle}>
              <span style={rowLabelStyle}>Row {ri + 1}</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button type="button" title="Move up" onClick={() => moveAvailRow(ri, -1)} style={iconBtn}><ChevronUp size={13} /></button>
                <button type="button" title="Move down" onClick={() => moveAvailRow(ri, 1)} style={iconBtn}><ChevronDown size={13} /></button>
                <button type="button" title="Delete row" onClick={() => removeAvailRow(ri)} style={iconBtn}><Trash2 size={13} /></button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ ...labelStyle, fontSize: 11 }}>Placement</label>
                <input type="text" value={r.label} onChange={e => updateAvailRow(ri, { label: e.target.value })} style={{ ...inputStyle, fontSize: 12 }} />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: 11 }}>Sub-label</label>
                <input type="text" value={r.note} onChange={e => updateAvailRow(ri, { note: e.target.value })} style={{ ...inputStyle, fontSize: 12 }} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, cellCount)}, 1fr)`, gap: 10 }}>
              {r.cells.map((c, ci) => (
                <div key={ci} style={{ border: '1px solid var(--color-border)', borderRadius: 6, padding: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 6 }}>
                    {availability.columns[ci + 1] || `Column ${ci + 2}`}
                  </div>
                  <button
                    type="button"
                    onClick={() => updateAvailCell(ri, ci, { on: !c.on })}
                    style={{
                      ...smallBtn, width: '100%', justifyContent: 'center', marginBottom: 6,
                      background: c.on ? 'var(--color-primary, #dc2626)' : 'transparent',
                      color: c.on ? '#fff' : 'var(--color-text-muted)',
                      border: '1px solid ' + (c.on ? 'transparent' : 'var(--color-border)'),
                      fontWeight: 600,
                    }}
                  >
                    {c.on ? '✓ Available' : '— Not available'}
                  </button>
                  <input
                    type="text"
                    value={c.note}
                    onChange={e => updateAvailCell(ri, ci, { note: e.target.value })}
                    placeholder="Short note (optional)"
                    style={{ ...inputStyle, fontSize: 11, padding: '6px 8px' }}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        <button type="button" onClick={addAvailRow} style={smallBtn}>
          <Plus size={12} /> Add row
        </button>
      </div>

      {/* Signature examples */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>Signature sponsorship examples</h3>
        <Toggle on={signature.enabled} onClick={() => setSignature(p => ({ ...p, enabled: !p.enabled }))}>
          Show the “Own a Memorable Part of the Broadcast” cards.
        </Toggle>
        <Field label="Heading" value={signature.heading} onChange={v => setSignature(p => ({ ...p, heading: v }))} />
        <Area label="Intro" value={signature.intro} onChange={v => setSignature(p => ({ ...p, intro: v }))} rows={2} />

        {signature.items.map((it, i) => (
          <div key={i} style={subCardStyle}>
            <div style={rowHeadStyle}>
              <span style={rowLabelStyle}>Example {i + 1}</span>
              <button
                type="button"
                title="Delete"
                onClick={() => setSignature(p => ({ ...p, items: p.items.filter((_, j) => j !== i) }))}
                style={iconBtn}
              >
                <Trash2 size={13} />
              </button>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ ...labelStyle, fontSize: 11 }}>Name</label>
              <input type="text" value={it.name} onChange={e => updateSigItem(i, { name: e.target.value })} style={{ ...inputStyle, fontSize: 12 }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ ...labelStyle, fontSize: 11 }}>How it appears</label>
                <textarea value={it.how} onChange={e => updateSigItem(i, { how: e.target.value })} rows={3} style={{ ...textareaStyle, fontSize: 12 }} />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: 11 }}>Value to the sponsor</label>
                <textarea value={it.value} onChange={e => updateSigItem(i, { value: e.target.value })} rows={3} style={{ ...textareaStyle, fontSize: 12 }} />
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setSignature(p => ({ ...p, items: [...p.items, { name: 'New feature', how: '', value: '' }] }))}
          style={smallBtn}
        >
          <Plus size={12} /> Add example
        </button>
      </div>

      {/* CTA */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>Closing call to action</h3>
        <Toggle on={cta.enabled} onClick={() => setCta(p => ({ ...p, enabled: !p.enabled }))}>
          Show the closing CTA block.
        </Toggle>
        <Field label="Heading" value={cta.heading} onChange={v => setCta(p => ({ ...p, heading: v }))} />
        <Area label="Body" value={cta.body} onChange={v => setCta(p => ({ ...p, body: v }))} rows={3} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field
            label="Primary button"
            value={cta.primary_label}
            onChange={v => setCta(p => ({ ...p, primary_label: v }))}
            hint="Scrolls to the sponsorship offers further down the page."
          />
          <Field
            label="Secondary button"
            value={cta.secondary_label}
            onChange={v => setCta(p => ({ ...p, secondary_label: v }))}
            hint="Opens an email to the contact address above."
          />
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          onClick={save}
          disabled={saving}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--color-primary, #dc2626)',
            color: '#fff', border: 'none', borderRadius: 6, padding: '9px 16px', fontSize: 13, fontWeight: 600,
            cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1,
          }}
        >
          <Save size={15} /> {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={restoreDefaults} disabled={saving} style={smallBtn}>
          <RotateCcw size={13} /> Restore defaults
        </button>
        <a
          href="https://watch.tenband.tv/ad-placements"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-muted)' }}
        >
          <ExternalLink size={13} /> Preview the Placements page
        </a>
      </div>
      {status && <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '10px 0 0' }}>{status}</p>}
    </div>
  )
}
